import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { isPathIndexable } from "@/config/markets";

/**
 * Injects SEO `<link rel="alternate" hreflang="...">` tags into <head>
 * for each supported language, plus an `x-default` fallback.
 *
 * Updates on every route change so the canonical URLs always reflect the
 * current path. Uses `window.location.origin` so it works across preview,
 * staging and production domains without hardcoding.
 *
 * Public routes only emit hreflang — authenticated routes (/dashboard,
 * /inbox, etc.) are skipped because they aren't crawlable content anyway.
 */
const PUBLIC_ROUTES = new Set(["/", "/pricing", "/login", "/legal", "/contact", "/mieterselbstauskunft", "/loyer-verite"]);
const TAG_MARKER = "data-lf-hreflang";
const ROBOTS_MARKER = "data-lf-market-robots";

// Pull from i18n SUPPORTED_LANGUAGES — already includes 'de'.

function clearExistingTags() {
  document
    .querySelectorAll(`link[${TAG_MARKER}]`)
    .forEach((el) => el.parentNode?.removeChild(el));
}

/**
 * Pages of a deactivated market stay reachable but must not be indexed.
 * The tag is added only on those paths and removed everywhere else, so the
 * document's own robots meta is never touched.
 */
function applyMarketRobots(pathname) {
  document
    .querySelectorAll(`meta[${ROBOTS_MARKER}]`)
    .forEach((el) => el.parentNode?.removeChild(el));

  if (isPathIndexable(pathname)) return;

  const meta = document.createElement("meta");
  meta.setAttribute("name", "robots");
  meta.setAttribute("content", "noindex,follow");
  meta.setAttribute(ROBOTS_MARKER, "true");
  document.head.appendChild(meta);
}

function buildHref(origin, pathname, lang) {
  // Use `?lang=xx` because LeaseFlowr's i18n detector reads the querystring.
  // Switching to path-based routing (/fr/, /en/) would require router changes.
  const url = new URL(pathname || "/", origin);
  if (lang) url.searchParams.set("lang", lang);
  return url.toString();
}

export default function HrefLangTags() {
  const location = useLocation();

  useEffect(() => {
    try {
      clearExistingTags();
      applyMarketRobots(location.pathname);

      if (!PUBLIC_ROUTES.has(location.pathname)) return;

      const origin = window.location.origin;
      const head = document.head;

      SUPPORTED_LANGUAGES.forEach((lang) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", buildHref(origin, location.pathname, lang));
        link.setAttribute(TAG_MARKER, "true");
        head.appendChild(link);
      });

      // x-default — served when no other locale matches. Points to the
      // canonical URL without `?lang=` so the detector picks browser default.
      const defaultLink = document.createElement("link");
      defaultLink.setAttribute("rel", "alternate");
      defaultLink.setAttribute("hreflang", "x-default");
      defaultLink.setAttribute("href", buildHref(origin, location.pathname, null));
      defaultLink.setAttribute(TAG_MARKER, "true");
      head.appendChild(defaultLink);
    } catch (err) {
      /* hreflang is SEO only, never block rendering */
      console.debug("HrefLangTags inject failed", err);
    }

    return clearExistingTags;
  }, [location.pathname]);

  return null;
}
